import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { HiOutlineChevronRight, HiOutlineX, HiOutlinePlus, HiOutlineExclamation, HiOutlineInformationCircle, HiOutlineTrash } from "react-icons/hi";
import { PiClockCounterClockwise } from "react-icons/pi";
import { useQuery } from "@tanstack/react-query";

import API from "../../api";
import { capture } from "../../instrument";
import { EXAM_TYPE, EXAM_TYPE_COLOR_BG, EXAM_TYPE_COLOR_TEXT, isShowingDeleted, readListState, writeListState } from "../../utils";
import PageLoadingSpinner from "../../components/PageLoadingSpinner";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useHotkeys } from "react-hotkeys-hook";
import useListShortcuts from "../../hooks/useListShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import { rankBySearch } from "../../utils/search";
import useSavedViews from "../../hooks/useSavedViews";
import useSearchFilters, { SEARCH_OPERATOR } from "../../hooks/useSearchFilters";
import SearchFilters from "../../components/SearchFilters";
import SaveViewModal from "../../components/SaveViewModal";
import ViewTabs from "../../components/ViewTabs";

const DEFAULT_EXAM_TYPE_PRIORITY = [
  EXAM_TYPE.DEFAULT,
  EXAM_TYPE.SANG,
  EXAM_TYPE.URINE,
  EXAM_TYPE.ECHANTILLON,
  EXAM_TYPE.H24,
  EXAM_TYPE.SEROLOGIE,
  EXAM_TYPE.PCR,
  EXAM_TYPE.SELLE,
  EXAM_TYPE.AUTRE,
];

const DEFAULT_VIEWS = [
  { _id: "ALL", name: "Toutes", filters: [] },
  { _id: "ALERTS", name: "Alertes", filters: [] },
];

const STORAGE_KEY = "table-list-state";

const Code = ({ user }) => {
  const history = useHistory();
  const savedState = useMemo(() => readListState(STORAGE_KEY), []);
  const [selectedStats, setSelectedStats] = useState();
  const [search, setSearch] = useState(savedState?.search || "");
  const [showDeleted, setShowDeleted] = useState(isShowingDeleted(savedState?.filters));

  const {
    data: rawTable = [],
    isLoading: isLoadingTable,
    isError: isErrorTable,
  } = useQuery({
    queryKey: ["tables", { showDeleted }],
    queryFn: async () => {
      const response = await API.get(`/table?includeDeleted=${showDeleted}`);
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }

      const table = response.data;
      return table.map((el) => {
        const groupCodes = {};
        el.codes.forEach((code) => {
          if (!groupCodes[code.type]) groupCodes[code.type] = [];
          groupCodes[code.type].push(code);
        });

        const groupExams = {};
        el.exams.forEach((exam) => {
          if (!groupExams[exam.type]) groupExams[exam.type] = [];
          groupExams[exam.type].push(exam);
        });

        return { ...el, groupCodes, groupExams };
      });
    },
  });

  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ["table-stats"],
    queryFn: async () => {
      const response = await API.get("/table/stats");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    enabled: rawTable.length > 0,
  });

  const table = useMemo(() => {
    if (!statsData) return rawTable;
    return rawTable.map((el) => {
      const exams = el.exams.map((exam) => ({
        ...exam,
        stats: statsData.find((stat) => stat._id === exam._id),
      }));
      const warning = exams.reduce((acc, exam) => (exam?.stats?.percentage > acc && exam?.stats?.countOnlyDeleted > 3 ? exam?.stats?.percentage : acc), 0);
      return { ...el, exams, warning };
    });
  }, [rawTable, statsData]);

  const filterFields = useMemo(
    () => [
      {
        key: "deleted",
        label: "Supprimée",
        operators: [SEARCH_OPERATOR.IS],
        values: ["Oui", "Non"],
        resolve: (el) => (el.deleted ? "Oui" : "Non"),
      },
    ],
    [],
  );

  const { filters: activeFilters, setFilters: setActiveFilters, applyFilters } = useSearchFilters(filterFields, { initialFilters: savedState?.filters });

  const onFiltersChange = (nextFilters) => {
    setActiveFilters(nextFilters);
    setShowDeleted(isShowingDeleted(nextFilters));
    resetSelection();
  };

  const { savedViews, activeViewId, saveModalOpen, setSaveModalOpen, handleSelectView, isViewActive, handleConfirmSaveView, handleDeleteView, saveOptions } = useSavedViews({
    collectionName: "table",
    defaultViews: DEFAULT_VIEWS,
    activeFilters,
    setActiveFilters: onFiltersChange,
    initialViewId: savedState?.viewId,
  });

  useEffect(() => {
    writeListState(STORAGE_KEY, { search, filters: activeFilters, viewId: activeViewId });
  }, [search, activeFilters, activeViewId]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const action = urlParams.get("action");
    const codes = urlParams.get("codes");
    const exams = urlParams.get("exams");

    if (action === "apply-suggestion" && codes && table.length > 0) {
      const existingCodeEntry = table.find((entry) => entry.codes.some((code) => code.value === codes));
      if (existingCodeEntry?._id) history.replace(`/table/${existingCodeEntry._id}/edit?action=apply-suggestion&codes=${codes}${exams?.length > 0 ? `&exams=${exams}` : ""}`);
      else history.replace(`/table/add?codes=${codes}${exams?.length > 0 ? `&exams=${exams}` : ""}`);
    }
  }, [location.search, table]);

  useEffect(() => {
    if (selectedStats) setSelectedStats(table.find((el) => el._id === selectedStats._id));
  }, [table]);

  const alertCount = table.filter((el) => el.warning).length;

  const getViewBadge = (tab) => {
    if (tab._id === "ALL") return <span className="ml-1.5 text-xs text-gray-400">{table.length}</span>;
    if (tab._id === "ALERTS" && alertCount > 0) return <span className="ml-1.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-xs text-orange-600">{alertCount}</span>;
    return null;
  };

  const filteredTable = useMemo(() => {
    let result = applyFilters(table);

    if (activeViewId === "ALERTS") {
      result = result.filter((el) => el.warning > 0);
      result.sort((a, b) => b.warning - a.warning);
    }

    return rankBySearch(result, search, (el) => [...el.codes.map((c) => c.value), ...el.exams.map((e) => e.value)].filter(Boolean).join(" "));
  }, [table, search, activeFilters, activeViewId]);

  const listRef = useRef(null);
  const selectedIndexRef = useRef(savedState?.selectedIndex || 0);
  const [selectedIndex, setSelectedIndex] = useState(savedState?.selectedIndex || 0);

  const virtualizer = useVirtualizer({
    count: filteredTable.length,
    getScrollElement: () => listRef.current,
    initialOffset: savedState?.scrollTop || 0,
    estimateSize: () => 53,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  const resetSelection = () => {
    selectedIndexRef.current = 0;
    setSelectedIndex(0);
    virtualizer.scrollToIndex(0);
    writeListState(STORAGE_KEY, { scrollTop: 0, selectedIndex: 0 });
  };

  const handleNavigateDown = () => {
    const next = selectedIndexRef.current >= filteredTable.length - 1 ? 0 : selectedIndexRef.current + 1;
    selectedIndexRef.current = next;
    setSelectedIndex(next);
    virtualizer.scrollToIndex(next);
  };

  const handleNavigateUp = () => {
    const prev = selectedIndexRef.current <= 0 ? filteredTable.length - 1 : selectedIndexRef.current - 1;
    selectedIndexRef.current = prev;
    setSelectedIndex(prev);
    virtualizer.scrollToIndex(prev);
  };

  const onRowMouseMove = (event, index) => {
    const pointerDidNotMove = event.movementX === 0 && event.movementY === 0;
    if (pointerDidNotMove || selectedIndexRef.current === index) return;
    selectedIndexRef.current = index;
    setSelectedIndex(index);
  };

  useListShortcuts(
    {
      onUp: handleNavigateUp,
      onDown: handleNavigateDown,
      onOpen: ({ newTab } = {}) => {
        const el = filteredTable[selectedIndexRef.current];
        if (!el) return;
        if (newTab) window.open(`/table/${el._id}/edit`, "_blank");
        else navigateToTable(el);
      },
    },
    [filteredTable, virtualizer],
  );

  useHotkeys(SHORTCUTS.CREATE.keys, () => history.push("/table/add"), { preventDefault: true });

  const navigateToTable = (el) => {
    writeListState(STORAGE_KEY, { scrollTop: listRef.current.scrollTop, selectedIndex: filteredTable.indexOf(el) });
    history.push({ pathname: `/table/${el._id}/edit`, state: { filteredIds: filteredTable.map((t) => t._id) } });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    resetSelection();
  };

  if (isLoadingTable) {
    return <PageLoadingSpinner />;
  }

  if (isErrorTable) {
    return (
      <div className="flex min-h-[calc(100vh-60px)] flex-col items-center justify-center gap-3 bg-gray-50">
        <span className="text-sm text-red-500">Erreur lors du chargement des tables</span>
      </div>
    );
  }

  return (
    <>
      <div className="h-[calc(100vh-60px)] bg-gray-50 p-6">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <h1 className="text-xl font-semibold text-gray-900">Tables</h1>
            <ViewTabs
              defaultViews={DEFAULT_VIEWS}
              savedViews={savedViews}
              isViewActive={isViewActive}
              onSelectView={handleSelectView}
              onDeleteView={handleDeleteView}
              getViewBadge={getViewBadge}
            />
            {isLoadingStats && <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" title="Chargement des statistiques..." />}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
              onClick={() => window.open("/audit-log/table2", "_blank", "noopener,noreferrer,width=1000,height=800")}
              title="Journal d'audit"
            >
              <PiClockCounterClockwise className="h-5 w-5" />
            </button>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              onClick={() => {
                history.push("/table/add");
                notifyShortcut("CREATE");
              }}
            >
              <HiOutlinePlus className="h-4 w-4" />
              <span>Nouvelle table</span>
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <SearchFilters
            fields={filterFields}
            filters={activeFilters}
            onFiltersChange={onFiltersChange}
            search={search}
            onSearchChange={handleSearchChange}
            placeholder="Rechercher une table..."
            saveOptions={saveOptions}
          />

          <div ref={listRef} className="max-h-[calc(100vh-252px)] overflow-y-auto">
            {filteredTable.length > 0 && (
              <div style={{ height: totalHeight, position: "relative" }}>
                {virtualItems.map((virtualRow) => {
                  const el = filteredTable[virtualRow.index];
                  return (
                    <div
                      key={el._id}
                      data-active={virtualRow.index === selectedIndex || undefined}
                      onMouseMove={(event) => onRowMouseMove(event, virtualRow.index)}
                      style={{ position: "absolute", top: 0, left: 0, right: 0, height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                      className="group flex cursor-pointer items-center border-b border-gray-100 px-4 hover:bg-gray-50 data-[active=true]:bg-primary-50/50"
                      onClick={() => navigateToTable(el)}
                    >
                      <span className="min-w-0 truncate text-sm font-medium text-gray-900">{el.codes.map((c) => c.value).join(", ")}</span>

                      <div className="ml-auto flex flex-shrink-0 items-center gap-4 pl-4">
                        {el.deleted && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            <HiOutlineTrash className="h-3 w-3" />
                            Supprimée
                          </span>
                        )}
                        <span className="text-xs tabular-nums text-gray-400">
                          {el.exams.length} examen{el.exams.length !== 1 ? "s" : ""}
                        </span>
                        {el.warning ? (
                          <button
                            type="button"
                            className="rounded-md p-1 text-orange-500 transition-colors hover:bg-orange-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedStats(el);
                            }}
                          >
                            <HiOutlineExclamation className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="rounded-md p-1 text-gray-400 opacity-0 transition-colors hover:bg-gray-100 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedStats(el);
                            }}
                          >
                            <HiOutlineInformationCircle className="h-4 w-4" />
                          </button>
                        )}
                        <HiOutlineChevronRight className="h-4 w-4 text-gray-300" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {filteredTable.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <HiOutlineExclamation className="mb-3 h-8 w-8 text-gray-300" />
                <p className="text-sm">{search || activeFilters.some((f) => f.values.length > 0) ? "Aucun resultat pour les filtres appliques" : "Aucun code trouvé"}</p>
                {activeViewId === "ALERTS" && !search && <p className="mt-1 text-xs">Aucune alerte à afficher</p>}
              </div>
            )}
          </div>
        </div>

        {selectedStats && <StatsModal selectedStats={selectedStats} close={() => setSelectedStats(null)} key={selectedStats.percentage} />}
      </div>

      {saveModalOpen && (
        <SaveViewModal
          onClose={() => setSaveModalOpen(false)}
          onConfirm={(name) => {
            setSaveModalOpen(false);
            handleConfirmSaveView(name);
          }}
        />
      )}
    </>
  );
};

const StatsModal = ({ selectedStats, close }) => {
  const [groupCodes, setGroupCodes] = useState({});
  const [windowsOpen, setWindowsOpen] = useState([]);

  useEffect(() => {
    let groupCodes = {};

    for (const code of selectedStats.codes) {
      if (!groupCodes[code.type]) groupCodes[code.type] = [];
      groupCodes[code.type].push(code);
    }

    setGroupCodes(groupCodes);
  }, [selectedStats]);

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div
            className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
            onClick={() => {
              windowsOpen.forEach((el) => el.window.close());
              close();
            }}
          />
        </div>
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
          &#8203;
        </span>
        <div
          className="inline-block transform rounded-2xl bg-white p-6 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-7xl sm:align-middle"
          role="dialog"
          aria-modal="true"
        >
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Statistiques</h2>
            <button
              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
              onClick={() => {
                windowsOpen.forEach((el) => el.window.close());
                close();
              }}
            >
              <HiOutlineX className="text-xl" />
            </button>
          </div>

          <div className="mb-6 flex flex-wrap gap-2">
            {Object.keys(groupCodes)
              .sort((a, b) => DEFAULT_EXAM_TYPE_PRIORITY.indexOf(a) - DEFAULT_EXAM_TYPE_PRIORITY.indexOf(b))
              .map((type) => (
                <div key={type} className={`rounded-lg ${EXAM_TYPE_COLOR_BG[type]} px-3 py-2`}>
                  <span className={`text-xs font-medium ${EXAM_TYPE_COLOR_TEXT[type]}`}>{type}: </span>
                  <span className="text-sm">{groupCodes[type].map((code) => code.value).join(", ")}</span>
                </div>
              ))}
          </div>

          <div className="max-h-[65vh] overflow-auto overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Mots-clés
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Occurences
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                    % Suppression
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Exemples de prescriptions supprimées
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {selectedStats.exams
                  .sort((a, b) => {
                    if (a.stats?.percentage === undefined) return 1;
                    if (b.stats?.percentage === undefined) return -1;
                    return b.stats?.percentage - a.stats?.percentage;
                  })
                  .map((exam) => (
                    <tr key={exam._id} className="transition-colors hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className="text-sm font-medium text-gray-800">{exam.value}</span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <span className="text-sm text-gray-600">{exam.stats?.count || 0}</span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <span
                          className={`inline-flex h-7 w-12 items-center justify-center rounded-full text-sm font-semibold ${
                            exam.stats?.percentage > 5 ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"
                          }`}
                        >
                          {Math.round(exam.stats?.percentage) || 0}%
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {exam.stats?.prescriptionWithDeleted.map((prescription) => (
                            <button
                              key={prescription._id}
                              className="rounded-lg bg-gray-100 px-3 py-1.5 text-left text-xs transition-colors hover:bg-primary-50 hover:text-primary-600"
                              onClick={() => {
                                const newWindow = window.open(`/prescription/${prescription._id}`, "_blank");
                                setWindowsOpen((prev) => [...prev, { name: prescription._id, window: newWindow }]);
                              }}
                            >
                              <span className="text-gray-500">[{prescription.codes.join(", ")}]</span> {prescription.text}:{" "}
                              <span className="font-medium text-primary-500">{prescription.silAdmissionNumber}</span>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Code;
